import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { CONTACT, SERVICES } from "../lib/data";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/Reveal";

const INPUT_CLASS =
  "w-full border border-white/10 bg-[#0d0d0d] px-4 py-3.5 text-sm font-light text-[#F5F5F5] placeholder:text-[#5a5a5a] transition-colors duration-300 focus:border-gold focus:outline-none rounded-none";

const Label = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]"
  >
    {children}
  </label>
);

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex gap-5 border-b border-white/10 py-6">
    <Icon size={20} strokeWidth={1.5} className="mt-1 shrink-0 text-gold" />
    <div>
      <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#737373]">
        {label}
      </p>
      {Array.isArray(value) ? (
        <div className="mt-2 flex flex-col gap-1">
          {value.map((n) => (
            <a
              key={n}
              href={`tel:${n.replace(/\s/g, "")}`}
              className="whitespace-nowrap text-base font-light text-[#F5F5F5] transition-colors hover:text-gold"
            >
              {n}
            </a>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-base font-light leading-relaxed text-[#F5F5F5]">
          {value}
        </p>
      )}
    </div>
  </div>
);

export default function Kontak() {
  const [form, setForm] = useState({
    nama: "",
    telepon: "",
    email: "",
    layanan: SERVICES[0].judul,
    pesan: "",
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/consultations", {
        ...form,
        email: form.email.trim() || null,
      });
      toast.success(
        "Permohonan konsultasi terkirim. Tim kami akan menghubungi Anda dalam 1×24 jam kerja."
      );
      setForm({ nama: "", telepon: "", email: "", layanan: SERVICES[0].judul, pesan: "" });
    } catch (err) {
      toast.error(
        "Gagal mengirim permohonan. Silakan coba lagi atau hubungi kami via WhatsApp."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Kontak & Konsultasi"
        titleLines={[
          "Ceritakan persoalan Anda.",
          <span key="t" className="italic text-gold">
            Kami yang memetakan jalannya.
          </span>,
        ]}
        description="Isi formulir di bawah atau hubungi kami langsung via WhatsApp. Konsultasi awal tidak dipungut biaya dan sepenuhnya rahasia."
      />

      <section className="mx-auto w-full max-w-7xl px-6 pb-32 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-5">
          <FadeUp className="lg:col-span-2">
            <div>
              <InfoItem icon={MapPin} label="Kantor" value={CONTACT.alamat} />
              <InfoItem icon={Phone} label="Telepon" value={CONTACT.telepon} />
              <InfoItem icon={Phone} label="WhatsApp" value={CONTACT.whatsappDisplay} />
              <InfoItem icon={Mail} label="Email" value={CONTACT.email} />
              <InfoItem icon={Clock} label="Jam Layanan" value={CONTACT.jam} />

              <div
                data-testid="map-placeholder"
                className="mt-10 flex aspect-[4/3] flex-col items-center justify-center gap-4 border border-gold/30 bg-[#0d0d0d] p-8 text-center"
              >
                <MapPin size={32} strokeWidth={1.25} className="text-gold" />
                <p className="font-serif text-2xl italic text-[#F5F5F5]">
                  Tebing Tinggi, Sumatera Utara
                </p>
                <p className="max-w-xs text-sm font-light text-[#A3A3A3]">
                  {CONTACT.alamat}
                </p>
                <a
                  data-testid="maps-link"
                  href="https://maps.google.com/?q=Jl.+Jend.+Ahmad+Yani+No.+47+Tebing+Tinggi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold transition-colors hover:text-gold-light"
                >
                  Buka di Google Maps <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-3">
            <form
              data-testid="consultation-form"
              onSubmit={submit}
              className="border border-white/10 bg-[#0A0A0A] p-8 lg:p-12"
            >
              <h2 className="font-serif text-3xl text-[#F5F5F5] lg:text-4xl">
                Formulir Konsultasi
              </h2>
              <p className="mt-3 text-sm font-light text-[#A3A3A3]">
                Seluruh informasi yang Anda kirimkan dijaga kerahasiaannya.
              </p>

              <div className="mt-10 grid gap-7 sm:grid-cols-2">
                <div>
                  <Label htmlFor="nama">Nama Lengkap *</Label>
                  <input
                    id="nama"
                    data-testid="konsultasi-nama-input"
                    required
                    minLength={2}
                    value={form.nama}
                    onChange={set("nama")}
                    placeholder="Nama Anda"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <Label htmlFor="telepon">Nomor Telepon / WhatsApp *</Label>
                  <input
                    id="telepon"
                    data-testid="konsultasi-telepon-input"
                    required
                    minLength={6}
                    value={form.telepon}
                    onChange={set("telepon")}
                    placeholder="08xx-xxxx-xxxx"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email (opsional)</Label>
                  <input
                    id="email"
                    data-testid="konsultasi-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="nama@email.com"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <Label htmlFor="layanan">Bidang Hukum *</Label>
                  <select
                    id="layanan"
                    data-testid="konsultasi-layanan-select"
                    value={form.layanan}
                    onChange={set("layanan")}
                    className={`${INPUT_CLASS} appearance-none`}
                  >
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.judul}>
                        {s.judul}
                      </option>
                    ))}
                    <option value="Bantuan Hukum Pro Bono">
                      Bantuan Hukum Pro Bono
                    </option>
                    <option value="Lainnya">Lainnya</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="pesan">Ceritakan Persoalan Anda *</Label>
                  <textarea
                    id="pesan"
                    data-testid="konsultasi-pesan-textarea"
                    required
                    minLength={10}
                    rows={6}
                    value={form.pesan}
                    onChange={set("pesan")}
                    placeholder="Jelaskan secara singkat masalah hukum yang Anda hadapi…"
                    className={`${INPUT_CLASS} resize-y`}
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="konsultasi-submit-button"
                disabled={sending}
                className="group mt-10 inline-flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? "Mengirim…" : "Kirim Permohonan"}
                {!sending && (
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                )}
              </button>
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
